import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

/** Return true to proceed with navigation; false to stay on Preferences. */
export type TryLeavePreferencesApprovals = () => Promise<boolean>;

type PreferencesApprovalsLeaveGuardContextValue = {
  registerTryLeave: (fn: TryLeavePreferencesApprovals | null) => void;
  tryLeave: () => Promise<boolean>;
};

const PreferencesApprovalsLeaveGuardContext =
  createContext<PreferencesApprovalsLeaveGuardContextValue | null>(null);

export function PreferencesApprovalsLeaveGuardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const tryLeaveRef = useRef<TryLeavePreferencesApprovals | null>(null);

  const registerTryLeave = useCallback(
    (fn: TryLeavePreferencesApprovals | null) => {
      tryLeaveRef.current = fn;
    },
    [],
  );

  const tryLeave = useCallback(async (): Promise<boolean> => {
    if (!tryLeaveRef.current) {
      return true;
    }
    return tryLeaveRef.current();
  }, []);

  const value = useMemo(
    () => ({ registerTryLeave, tryLeave }),
    [registerTryLeave, tryLeave],
  );

  return (
    <PreferencesApprovalsLeaveGuardContext.Provider value={value}>
      {children}
    </PreferencesApprovalsLeaveGuardContext.Provider>
  );
}

export function usePreferencesApprovalsLeaveGuard(): PreferencesApprovalsLeaveGuardContextValue | null {
  return useContext(PreferencesApprovalsLeaveGuardContext);
}

/** Register while Preferences is mounted; cleared on unmount. */
export function useRegisterPreferencesApprovalsLeaveGuard(
  tryLeave: TryLeavePreferencesApprovals | null,
): void {
  const guard = usePreferencesApprovalsLeaveGuard();

  useEffect(() => {
    if (!guard) {
      return;
    }
    guard.registerTryLeave(tryLeave);
    return () => guard.registerTryLeave(null);
  }, [guard, tryLeave]);
}
